import { Button, Heading, Icon, system, transition } from '@hover/blueprint';
import type { FlexProps } from '@hover/blueprint/chakra';
import { Flex, HStack } from '@hover/blueprint/chakra';
import { resetFocus } from '@hover/blueprint/foundation';
import { iX } from '@hover/icons';
import React from 'react';

import { PaletteCard } from 'src/modules/designStudio/components/DesignMenu/Menu/TemplateMenu/PaletteCard';
import type { TemplatePaletteConfig } from 'src/modules/designStudio/helpers/data/PaletteStyles';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

interface PaletteRowProps extends FlexProps {
  palettes: TemplatePaletteConfig[];
  selectedPaletteIndex: number;
  onPaletteSelected: (index: number) => void;
  onPaletteRowShowMore: () => void;
  onPaletteRowCancel: () => void;
}

export const PaletteRow = ({
  palettes,
  selectedPaletteIndex,
  onPaletteSelected,
  onPaletteRowCancel,
  onPaletteRowShowMore,
  ...props
}: PaletteRowProps) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);

  if (!palettes || palettes.length === 0) return null;

  const PREVIEW_COUNT = 3;
  const previewPalettes = palettes.slice(0, PREVIEW_COUNT);
  const remainingCount = palettes.length - previewPalettes.length;

  return (
    <Flex
      direction="column"
      gap={100}
      justifyContent="stretch"
      marginTop={300}
      {...props}
    >
      <HStack justifyContent="space-between" mb="100">
        <Heading marginBottom="0" size={200}>
          {t('menu.template.paletteMiniRowHeader')}
        </Heading>
        <Button
          fill="minimal"
          label="Hide Palettes"
          onClick={onPaletteRowCancel}
          shape="circle"
          size="small"
        >
          <Icon color="secondary600" icon={iX} size="small" />
        </Button>
      </HStack>
      <Flex alignItems="center" flexDirection="row" flexWrap="nowrap" gap="300">
        {previewPalettes.map((palette, index) => (
          <PaletteCard
            alignSelf="stretch"
            flexGrow={1}
            flexShrink={1}
            isSelected={index === selectedPaletteIndex}
            isSmall
            key={palette.paletteName}
            maxWidth={{ base: '400', tablet: '600' }}
            onClick={() => {
              onPaletteSelected(index);
            }}
            palette={palette}
            showName={false}
            width="100%"
          />
        ))}
        <system.button
          _hover={{ bg: 'neutral.300', color: 'neutral.900' }}
          bg="neutral.200"
          borderRadius="300"
          borderWidth="0"
          boxSizing="content-box"
          color="neutral.600"
          cursor="pointer"
          flexShrink={0}
          fontFamily="body"
          fontSize="200"
          fontWeight="bodyBold"
          height="200"
          onClick={onPaletteRowShowMore}
          p="200"
          width="200"
          {...transition.easing250('background-color')}
          sx={resetFocus({ shadow: 'ringPrimary' }, true)}
        >
          {remainingCount}+
        </system.button>
      </Flex>
    </Flex>
  );
};
